import React from "react";
import { Link } from "react-router-dom";
import { Contact, ContactType } from "@akfdev-com/shared";
import { toGbpDisplay } from "./gbp";

interface ContactListViewProps {
  contacts: Contact[];
}

const typeColors: Record<ContactType, { bg: string; text: string }> = {
  resident: { bg: "#ede9fe", text: "#6d28d9" },
  supplier: { bg: "#fef3c7", text: "#92400e" },
  customer: { bg: "#dbeafe", text: "#1e40af" },
};

/**
 * Contact List View
 *
 * Renders contacts as rows (~68px high) with:
 * - Type badge (with design-language hues)
 * - Name and contact info
 * - A–Z index (alphabetical navigation)
 * - Owed column (green #16a34a)
 * - Owing column (red #dc2626)
 *
 * All amounts displayed via toGbpDisplay with semantic colours.
 * Empty values show —.
 *
 * Validates: Requirements 8.1, 8.5
 */
export const ContactListView: React.FC<ContactListViewProps> = ({
  contacts,
}) => {
  // Group contacts by first letter for A–Z index
  const groupedByLetter = contacts.reduce((acc, contact) => {
    const letter = (contact.name || "X").charAt(0).toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(contact);
    return acc;
  }, {} as Record<string, Contact[]>);

  const letters = Object.keys(groupedByLetter).sort();

  return (
    <div className="space-y-6">
      {/* A–Z Index */}
      {letters.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {letters.map((letter) => (
            <a
              key={letter}
              href={`#letter-${letter}`}
              className="px-2 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              {letter}
            </a>
          ))}
        </div>
      )}

      {/* Contacts grouped by letter */}
      {letters.map((letter) => {
        const groupContacts = groupedByLetter[letter] || [];
        return (
          <div key={letter}>
            {/* Letter header */}
            <h3
              id={`letter-${letter}`}
              className="text-sm font-semibold text-gray-700 mb-2"
            >
              {letter}
            </h3>

            {/* List rows */}
            <div className="bg-white shadow rounded-lg divide-y">
              {groupContacts.map((contact) => (
                <Link
                  key={contact.id}
                  to={`/dashboard/contacts/${contact.id}`}
                  className="px-4 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-4"
                  style={{ minHeight: "68px" }}
                >
                  {/* Left: name + type badge */}
                  <div className="flex-1 flex items-center gap-3">
                    <div className="flex-shrink-0 h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-gray-700">
                        {contact.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {contact.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: typeColors[contact.type].bg,
                            color: typeColors[contact.type].text,
                          }}
                        >
                          {contact.type.charAt(0).toUpperCase() +
                            contact.type.slice(1)}
                        </span>
                        {(contact.email || contact.phone) && (
                          <span className="text-xs text-gray-500">
                            {contact.email || contact.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: owed and owing columns */}
                  <div className="flex-shrink-0 flex gap-6 text-right">
                    {/* Owed (to us, green) */}
                    <div>
                      <p className="text-xs text-gray-500">Owed</p>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "#16a34a" }}
                      >
                        £{toGbpDisplay(contact.totalOwed || 0)}
                      </p>
                    </div>

                    {/* Owing (by us, red) */}
                    <div>
                      <p className="text-xs text-gray-500">Owing</p>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "#dc2626" }}
                      >
                        £{toGbpDisplay(contact.totalOwing || 0)}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
